import { Check, X, ArrowRight } from "lucide-react";

/**
 * Mobile package tier card — used inside the Embla carousel that stacks
 * ALL audiences (creators + brands) into a single infinite-loop strip.
 *
 * Props:
 *   tier: shape from AUDIENCES[*].tiers with .audienceId and .audienceLabel
 *         attached by the parent (Packages.jsx flatTiers memo).
 *   index: 0-based position within the flattened list (used for the tiny
 *          B01/C01 label so each audience restarts at 1).
 */
export const PackageCardMobile = ({ tier: t, index: i }) => (
  <div
    data-tier-card
    data-audience={t.audienceId}
    data-testid={`package-${t.audienceId}-tier-${i}`}
    style={{ transform: "translateZ(0)", backfaceVisibility: "hidden" }}
    className={`relative rounded-3xl p-7 flex flex-col shrink-0 basis-[86%] max-w-[360px] ml-4 ${
      t.highlight
        ? "bg-[#11091a] border border-[#00f0ff]/40"
        : "bg-[#0a0510] border border-white/10"
    }`}
  >
    <div className="relative">
      <div className="flex items-center justify-between mb-6">
        <div
          className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-[10px] uppercase tracking-[0.28em] font-semibold border"
          style={{
            color: t.accent,
            borderColor: `${t.accent}40`,
            background: `${t.accent}12`,
          }}
        >
          <t.icon size={12} /> {t.badge}
        </div>
        <div className="font-mono-tight text-[10px] text-white/40">
          {t.audienceId === "brands" ? "B" : "C"}0{(i % 3) + 1}
        </div>
      </div>
      <div className="font-display font-bold text-[28px] tracking-tight">
        {t.name}
      </div>
      <div className="text-[#a395c3] text-sm mt-2 min-h-[42px]">
        {t.description}
      </div>
      <div className="mt-6 flex items-baseline gap-2">
        <span className="font-display font-black text-5xl tracking-tighter">
          ${t.price}
        </span>
        <span className="text-[#a395c3] text-sm">{t.unit}</span>
      </div>
      <a
        href="#book"
        onClick={(e) => {
          e.preventDefault();
          window.dispatchEvent(
            new CustomEvent("sb:open-booking", { detail: { tier: t.name } })
          );
        }}
        data-testid={`package-${t.audienceId}-tier-${i}-cta`}
        className="mt-6 w-full inline-flex items-center justify-center gap-2 py-3.5 rounded-full bg-white text-[#05050a] font-semibold text-sm hover:bg-[#00f0ff] transition-colors"
      >
        {t.cta} <ArrowRight size={14} />
      </a>
      {t.pkg && t.pkg !== "custom" && (
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            window.dispatchEvent(new CustomEvent("sb:open-buy", { detail: { package_key: t.pkg } }));
          }}
          data-testid={`package-buy-mobile-${t.audienceId}-${i}`}
          className="mt-2 w-full inline-flex items-center justify-center gap-2 py-2.5 rounded-full text-xs text-white/70 hover:text-white bg-transparent border border-white/10 hover:border-[#22c55e]/40 hover:bg-[#22c55e]/[0.06] transition-all"
        >
          Buy now →
        </button>
      )}
    </div>
    <div className="mt-6 pt-6 border-t border-white/10">
      <div className="text-[10px] uppercase tracking-[0.28em] text-white/50 mb-3">
        What&apos;s included
      </div>
      <ul className="space-y-2.5 text-[13px] text-white/85">
        {t.features.map((f) => (
          <li key={f.label} className="flex items-start gap-2.5">
            {f.in ? (
              <Check
                size={14}
                className="text-[#00f0ff] mt-0.5 flex-shrink-0"
                strokeWidth={2.5}
              />
            ) : (
              <X
                size={14}
                className="text-white/30 mt-0.5 flex-shrink-0"
                strokeWidth={2}
              />
            )}
            <span>{f.label}</span>
          </li>
        ))}
      </ul>
    </div>
  </div>
);

export default PackageCardMobile;
